import type { CourierSettlementLinesFilters, SettlementWeightCompare } from '../../../models/settlementLinesFilters';
import {
  BILLING_FILTER_VALUES,
  WEIGHT_COMPARE_VALUES,
  cloneSettlementLinesFilters,
  countSettlementLinesPanelFilters,
} from '../../../models/settlementLinesFilters';
import { ShopsApiAdapter } from '@modules/Orders/List/adapters/ShopsApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { MultiSelect } from '@shared/components/ui/MultiSelect/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { apiClientWH } from '@shared/services/ApiClient';
import { cn } from '@shared/utils/utils';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface SettlementLinesFiltersPanelProps {
  filters: CourierSettlementLinesFilters;
  onChange: (next: CourierSettlementLinesFilters) => void;
  hasTeamData?: boolean;
  className?: string;
}

/**
 * Panel de filtros de líneas de liquidación (mismo patrón que OrdersFiltersPanel).
 */
export const SettlementLinesFiltersPanel = ({
  filters,
  onChange,
  hasTeamData = false,
  className,
}: SettlementLinesFiltersPanelProps) => {
  const { t } = useTranslation('courierInvoicesModule');
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const shopsAdapter = useMemo(() => new ShopsApiAdapter(httpClient), [httpClient]);

  const shipmentStatusesQuery = useQuery({
    queryKey: ['courier-settlement-lines-filter-shipment-statuses'],
    queryFn: async () => {
      const raw = await httpClient.get<{ data?: unknown[] } | unknown[]>('/shipment-statuses');
      const list = Array.isArray(raw) ? raw : ((raw as { data?: unknown[] }).data ?? []);
      return list.map((s: Record<string, unknown>) => ({
        id: String(s.id),
        name: String(s.name ?? s.description ?? s.id),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const loadShopsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }) => {
      const result = await shopsAdapter.getShopsPaginated({
        search: params.search,
        page: params.page,
        limit: params.limit,
        includeTeam: hasTeamData,
      });
      return {
        options: result.shops.map((s) => ({
          value: s.id,
          label: s.name,
          description: hasTeamData ? s.team?.name : undefined,
        })),
        hasMore: result.hasMore,
      };
    },
    [shopsAdapter, hasTeamData],
  );

  const loadTeamsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }) => {
      const queryParams: Record<string, number | string> = {
        page: params.page,
        per_page: params.limit,
      };
      if (params.search) queryParams.name = params.search;
      const response = await httpClient.get<{
        data?: Record<string, unknown>[];
        meta?: Record<string, unknown>;
      }>('/teams-child', queryParams);
      const list = Array.isArray(response) ? (response as unknown[]) : (response.data ?? []);
      const meta = !Array.isArray(response) ? (response.meta ?? {}) : {};
      const currentPage = Number(meta.current_page ?? meta.currentPage ?? 1);
      const lastPage = Number(meta.last_page ?? meta.lastPage ?? 1);
      return {
        options: list.map((row: Record<string, unknown>) => ({
          value: String(row.id),
          label: String(row.name ?? row.id),
        })),
        hasMore: meta ? currentPage < lastPage : false,
      };
    },
    [httpClient],
  );

  const shipmentStatusOptions = useMemo(() => {
    const rows = shipmentStatusesQuery.data ?? [];
    return rows.map((s) => ({ value: s.id, label: s.name }));
  }, [shipmentStatusesQuery.data]);

  const billingEstadoOptions = useMemo(
    () =>
      BILLING_FILTER_VALUES.map((v) => ({
        value: v,
        label:
          v === 'facturado'
            ? t('settlement.lines_filters.billing.facturado')
            : v === 'por facturar'
              ? t('settlement.lines_filters.billing.por_facturar')
              : t('settlement.lines_filters.billing.no_facturado'),
      })),
    [t],
  );

  const weightCompareOptions = useMemo(
    () =>
      WEIGHT_COMPARE_VALUES.map((v) => ({
        value: v,
        label: t(`settlement.lines_filters.weight.${v}`, v),
      })),
    [t],
  );

  const matchMode = filters.matched === true ? 'matched' : filters.matched === false ? 'unmatched' : 'all';

  const setMatchMode = (mode: string) => {
    const next = { ...filters };
    if (mode === 'all') {
      delete next.matched;
    } else if (mode === 'matched') {
      next.matched = true;
    } else {
      next.matched = false;
    }
    onChange(cloneSettlementLinesFilters(next));
  };

  const setBillingEstadosFromMultiSelect = (vals: string[]) => {
    const allowed = new Set<string>([...BILLING_FILTER_VALUES]);
    const filtered = vals.filter((v) => allowed.has(v));
    const next = { ...filters };
    if (!filtered.length) delete next.estados;
    else next.estados = filtered;
    onChange(cloneSettlementLinesFilters(next));
  };

  const setWeightComparesFromMultiSelect = (vals: string[]) => {
    const allowed = new Set<string>([...WEIGHT_COMPARE_VALUES]);
    const filtered = vals.filter((v) => allowed.has(v));
    const next = { ...filters };
    if (!filtered.length) delete next.weight_compares;
    else next.weight_compares = filtered as SettlementWeightCompare[];
    onChange(cloneSettlementLinesFilters(next));
  };

  const clearPanelFilters = () => {
    const search = filters.search?.trim();
    onChange(cloneSettlementLinesFilters(search ? { search: search.slice(0, 255) } : {}));
  };

  const panelFiltersCount = countSettlementLinesPanelFilters(filters);

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('settlement.lines_filters.match.label')}>
            <Select value={matchMode} onValueChange={setMatchMode}>
              <SelectTrigger
                id="slf-match"
                className="h-[38px] w-full bg-background border-input transition-colors"
                data-testid="settlement-lines-filters-match-select"
              >
                <SelectValue placeholder={t('settlement.lines_filters.match.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="settlement-lines-filters-match-all">
                  {t('settlement.lines_filters.match.all')}
                </SelectItem>
                <SelectItem value="matched" data-testid="settlement-lines-filters-match-matched">
                  {t('settlement.lines_filters.match.matched')}
                </SelectItem>
                <SelectItem value="unmatched" data-testid="settlement-lines-filters-match-unmatched">
                  {t('settlement.lines_filters.match.unmatched')}
                </SelectItem>
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField label={t('settlement.lines_filters.shops')}>
            <MultiSelect
              loadOptions={loadShopsForMultiSelect}
              value={(filters.shop_ids ?? []).map(String)}
              onValueChange={(vals) => {
                const next = { ...filters };
                if (!vals.length) delete next.shop_ids;
                else next.shop_ids = vals.map((x) => Number(x));
                onChange(cloneSettlementLinesFilters(next));
              }}
              placeholder={t('settlement.lines_filters.shops_placeholder')}
              searchPlaceholder={t('settlement.lines_filters.search_options')}
              dataTestId="settlement-lines-filters-shops"
              triggerClassName="h-[38px]"
              pageSize={20}
            />
          </FilterField>

          <FilterField label={t('settlement.lines_filters.teams')}>
            <MultiSelect
              loadOptions={loadTeamsForMultiSelect}
              value={(filters.team_ids ?? []).map(String)}
              onValueChange={(vals) => {
                const next = { ...filters };
                if (!vals.length) delete next.team_ids;
                else next.team_ids = vals.map((x) => Number(x));
                onChange(cloneSettlementLinesFilters(next));
              }}
              placeholder={t('settlement.lines_filters.teams_placeholder')}
              searchPlaceholder={t('settlement.lines_filters.search_options')}
              dataTestId="settlement-lines-filters-teams"
              triggerClassName="h-[38px]"
              pageSize={20}
            />
          </FilterField>

          <FilterField label={t('settlement.lines_filters.order_statuses')}>
            <MultiSelect
              options={billingEstadoOptions}
              value={(filters.estados ?? []).map(String)}
              onValueChange={setBillingEstadosFromMultiSelect}
              placeholder={t('settlement.lines_filters.order_statuses_placeholder')}
              emptyText={t('settlement.lines_filters.not_found')}
              dataTestId="settlement-lines-filters-order-statuses"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('settlement.lines_filters.shipment_statuses')}>
            <MultiSelect
              options={shipmentStatusOptions}
              value={(filters.shipment_status_ids ?? []).map(String)}
              onValueChange={(vals) => {
                const next = { ...filters };
                if (!vals.length) delete next.shipment_status_ids;
                else next.shipment_status_ids = vals.map((x) => Number(x));
                onChange(cloneSettlementLinesFilters(next));
              }}
              placeholder={
                shipmentStatusesQuery.isLoading
                  ? t('loading.detail')
                  : t('settlement.lines_filters.shipment_statuses_placeholder')
              }
              emptyText={t('settlement.lines_filters.not_found')}
              disabled={shipmentStatusesQuery.isLoading}
              dataTestId="settlement-lines-filters-shipment-statuses"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('settlement.lines_filters.weight.label')}>
            <MultiSelect
              options={weightCompareOptions}
              value={(filters.weight_compares ?? []).map(String)}
              onValueChange={setWeightComparesFromMultiSelect}
              placeholder={t('settlement.lines_filters.weight.placeholder')}
              emptyText={t('settlement.lines_filters.not_found')}
              dataTestId="settlement-lines-filters-weight-compares"
              triggerClassName="h-[38px]"
            />
          </FilterField>
        </div>
      </FilterSection>

      {panelFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearPanelFilters}
            type="button"
          >
            {panelFiltersCount === 1
              ? t('settlement.lines_filters.clear_one')
              : t('settlement.lines_filters.clear_many', { count: panelFiltersCount })}
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ children }: { children: ReactNode }) => <div className="space-y-3">{children}</div>;

const FilterField = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
